import { useAtom, useAtomValue } from 'jotai';
import type { TrackerOpenTarget } from 'OpalChat/Tracker';
import {
  type PointerEvent as ReactPointerEvent,
  Suspense,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useLocation } from 'react-router-dom';
import { IconThumbtack, IconThumbtackSolid, IconXmark } from '@optiaxiom/icons';
import { Box, Button, Flex, Heading, Spinner, theme, Tooltip } from '@optiaxiom/react';

import { opalAuthAtom, opalOverlayOpenAtom } from '@frontend/atoms';
import {
  STICKYBOARD_DRAWER_WIDTHS,
  stickyboardDrawerModeAtom,
  stickyboardDrawerVisibleAtom,
  stickyboardDrawerWidthAtom,
} from '@frontend/atoms/stickyboardAtoms';
import { OVERLAY_PAGE_SEGMENTS } from '@frontend/constants';
import { useNamespacedNavigate } from '@frontend/hooks/useNamespacedNavigate';
import { useOpalUser } from '@frontend/hooks/useOpalUser';
import { SIDEBAR_WIDTH } from '@frontend/styles/layout.css';
import { buildMessagesPath } from '@frontend/utils/messagingUrl';

import { LazyTracker } from './LazyTracker';
import { hideScrollbar, trackerResizeHandleStyles } from './Stickyboard.css';

// Drag-resize bounds. Min keeps the board usable; max caps at the widest
// documented breakpoint so it never swallows the whole viewport.
const TRACKER_MIN_WIDTH = 360;
const TRACKER_MAX_WIDTH = STICKYBOARD_DRAWER_WIDTHS[STICKYBOARD_DRAWER_WIDTHS.length - 1];
const clampTrackerWidth = (value: number) =>
  Math.max(TRACKER_MIN_WIDTH, Math.min(TRACKER_MAX_WIDTH, value));

type Props = {
  /**
   * Geometry of the content stage (measured by BaseLayout). The drawer is a
   * fixed, high z-index overlay aligned to the content area's left edge — so it
   * sits above the chat MFE and chat overlay (which escape the stage's
   * stacking context and would otherwise clip a drawer rendered inside it).
   */
  stageLeft?: number;
  stageTop?: number;
  stageHeight?: number;
};

/**
 * Tracker drawer (host chrome). Toggled from the top-toolbar checkbox button
 * (`TrackerToggleButton`, beside "Ask Opal"). The board itself is the federated
 * `OpalChat/Tracker` surface, mounted via `LazyTracker` — the host owns only the
 * slide-in panel, dock/float + close controls, and dismissal. The drawer is
 * Tracker's only surface (there is no full page).
 *
 * Two modes (toggled from the header, persisted in stickyboardDrawerModeAtom):
 *   - "peek" (float): overlays the content; no backdrop. An outside click (or
 *     Escape) dismisses it, like a popover.
 *   - "held" (dock): the content area shifts to fit (BaseLayout adds a margin
 *     equal to the drawer width); it stays open until closed.
 */
export function StickyboardDrawer({ stageTop = 0, stageHeight }: Props) {
  const [visible, setVisible] = useAtom(stickyboardDrawerVisibleAtom);
  const [width, setWidth] = useAtom(stickyboardDrawerWidthAtom);
  const [mode, setMode] = useAtom(stickyboardDrawerModeAtom);
  const [chatOpen, setChatOpen] = useAtom(opalOverlayOpenAtom);
  const { opalUser } = useOpalUser();
  const opalAuth = useAtomValue(opalAuthAtom);
  // Namespaced so buildMessagesPath's instance-relative path (/messages/…) is
  // prefixed with /instances/{id}; a plain navigate would miss the instance
  // scope and the router would bounce it to /chat.
  const navigate = useNamespacedNavigate();
  const { pathname } = useLocation();
  const panelRef = useRef<HTMLDivElement>(null);
  const resizeRef = useRef<{ startX: number; startWidth: number } | null>(null);

  const isPeek = mode === 'peek';

  // Is the "Ask Opal" chat overlay currently on screen? It renders only on
  // overlay pages and never on /chat (see OpalChatOverlay). When it is, shift
  // the Tracker left by the overlay's width so both trays sit side by side
  // instead of the Tracker (z1200) hiding the overlay (z1100). Mirrors the
  // overlay's own visibility gate so the offset appears/clears in lockstep.
  const segments = pathname.split('/');
  const chatOverlayShowing =
    chatOpen && !segments.includes('chat') && OVERLAY_PAGE_SEGMENTS.some(s => segments.includes(s));
  const rightOffset = chatOverlayShowing ? SIDEBAR_WIDTH : 0;

  // Drag the left edge to resize (right-anchored → dragging left widens).
  // Free during drag, snapped to the nearest documented breakpoint on release.
  const onResizePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      resizeRef.current = { startX: event.clientX, startWidth: width };
      const handleMove = (moveEvent: PointerEvent) => {
        const drag = resizeRef.current;
        if (!drag) return;
        setWidth(clampTrackerWidth(drag.startWidth + (drag.startX - moveEvent.clientX)));
      };
      const handleUp = () => {
        resizeRef.current = null;
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', handleUp);
        document.body.style.removeProperty('cursor');
        document.body.style.removeProperty('user-select');
        // Snap to the nearest of the documented breakpoints.
        setWidth(w =>
          STICKYBOARD_DRAWER_WIDTHS.reduce((a, b) => (Math.abs(b - w) < Math.abs(a - w) ? b : a))
        );
      };
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);
    },
    [width, setWidth]
  );

  // Deep-link a clicked linkable card to where it lives.
  const handleOpen = useCallback(
    (sticky: TrackerOpenTarget) => {
      if (sticky.item_type === 'opal_chat') {
        // Close the drawer first — the Ask Opal overlay (z 1100) is narrower
        // and sits below this drawer (z 1200), so leaving the drawer open would
        // hide it entirely and the click would look like a no-op.
        setVisible(false);
        setChatOpen(true);
        return;
      }
      if (sticky.item_type === 'im_message' && sticky.linked_thread_ref?.kind === 'im') {
        setVisible(false);
        // linked_thread_ref.thread_id is the conversation id, so it maps to
        // conversationId (not threadPanelId) — otherwise buildMessagesPath drops
        // it and returns the bare /messages list instead of the deep-link.
        navigate(
          buildMessagesPath({
            conversationId: sticky.linked_thread_ref.thread_id,
            // Present only for a clipped thread reply — opens its thread so the
            // reply is reachable (a bare messageId can't find a thread-only reply).
            threadPanelId: sticky.linked_thread_panel_id,
            targetMessageId: sticky.linked_message_id,
          })
        );
      }
      // hitl / other kinds: no dedicated route yet.
    },
    [navigate, setChatOpen, setVisible]
  );

  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setVisible(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, setVisible]);

  // Float (peek) mode dismisses on any outside interaction, like a popover.
  // Dock (held) mode stays put. The top-toolbar toggle is excluded so its own
  // click owns the toggle instead of double-firing.
  useEffect(() => {
    if (!visible || !isPeek) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!panelRef.current || panelRef.current.contains(target)) return;
      if (target?.closest?.('[data-testid="tracker-toggle"]')) return;
      setVisible(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [visible, isPeek, setVisible]);

  if (!visible) return null;

  return (
    <Box
      ref={panelRef}
      role="dialog"
      aria-label="Tracker"
      data-testid="stickyboard-drawer"
      style={{
        position: 'fixed',
        // 0 normally; shifted left by the Ask Opal overlay's width when it is
        // open so the two trays sit side by side instead of overlapping.
        right: rightOffset,
        top: stageTop,
        height: stageHeight ?? '100vh',
        width,
        maxWidth: '100%',
        background: theme.colors['bg.default'],
        borderLeft: `1px solid ${theme.colors['border.tertiary']}`,
        // Float (peek) lifts above the content with a shadow; dock (held) sits
        // flush in the space the content vacated, so no shadow.
        boxShadow: isPeek ? '-2px 0 16px rgba(0,0,0,0.12)' : 'none',
        // Above the chat overlay (1100) and the chat MFE's fixed elements.
        zIndex: 1200,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        aria-hidden
        data-testid="tracker-resize-handle"
        className={trackerResizeHandleStyles}
        onPointerDown={onResizePointerDown}
      />
      <Flex
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        style={{ padding: 16, borderBottom: `1px solid ${theme.colors['border.tertiary']}` }}
      >
        <Heading level="3">Tracker</Heading>
        <Flex flexDirection="row" gap="4" alignItems="center">
          <Tooltip content={isPeek ? 'Dock' : 'Float'}>
            <Button
              size="sm"
              appearance="subtle"
              aria-label={isPeek ? 'Dock' : 'Float'}
              icon={isPeek ? <IconThumbtack /> : <IconThumbtackSolid />}
              onClick={() => setMode(isPeek ? 'held' : 'peek')}
            />
          </Tooltip>
          <Tooltip content="Close">
            <Button
              size="sm"
              appearance="subtle"
              aria-label="Close"
              icon={<IconXmark />}
              onClick={() => setVisible(false)}
            />
          </Tooltip>
        </Flex>
      </Flex>
      <Box className={hideScrollbar} style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {opalUser && opalAuth ? (
          <Suspense
            fallback={
              <Flex style={{ padding: 16 }}>
                <Spinner aria-label="Loading" />
              </Flex>
            }
          >
            <LazyTracker
              currentUser={{
                instanceId: opalUser.instance.instance_id,
                avatarUrl: '',
                name: `${opalUser.firstname} ${opalUser.lastname}`,
                operations: opalUser.instance.operations
                  .filter(op => op.allowed)
                  .map(op => op.name),
              }}
              opalAuth={opalAuth}
              onOpen={handleOpen}
            />
          </Suspense>
        ) : (
          <Flex style={{ padding: 16 }}>
            <Spinner aria-label="Loading" />
          </Flex>
        )}
      </Box>
    </Box>
  );
}
